import { isHttpSecure } from "lib/utils/utils";
import { joinUrl } from "lib/utils/connection";
import { gatewayServiceBaseUrl } from "lib/consts/arcGateway";
import { authedFetch } from "lib/utils/authedFetch";
import { messageFromFailedResponse } from "lib/utils/userFacingApiError";

/** HTTP gimbal gateway on the robot (gimbal_gateway FastAPI, default port 12400). */

const DEFAULT_GIMBAL_GATEWAY_PORT = "12400";

export type GimbalGatewayProfileId = "skydroid" | "zr10";

export type GimbalGatewayProfileResponse = {
    ok?: boolean;
    profile?: string;
    gateway_yaml?: string;
};

export type GimbalGatewayCurrentProfileResponse = {
    ok?: boolean;
    profile?: string | null;
    gateway_yaml?: string;
};

/** Base URL with no trailing slash. Uses the selected ARC host like Wi‑Fi / Ethernet APIs. */
export function getGimbalGatewayBaseUrl(selectedArcHost?: string | null, isSecure: boolean = isHttpSecure(), arcId?: string | null): string | null {
    const explicit = process.env.REACT_APP_GIMBAL_GATEWAY_BASE_URL?.trim();
    if (explicit) {
        return explicit.replace(/\/$/, "");
    }
    const port =
        process.env.REACT_APP_GIMBAL_GATEWAY_PORT?.trim() || DEFAULT_GIMBAL_GATEWAY_PORT;
    const proxyHost =
        selectedArcHost?.trim() ||
        process.env.REACT_APP_GIMBAL_GATEWAY_HOST?.trim();
    const host =
        proxyHost ||
        process.env.REACT_APP_SINGLE_ARC_HOSTNAME?.trim() ||
        process.env.REACT_APP_HOST_OVERRIDE?.trim() ||
        (typeof window !== "undefined" ? window.location.hostname : "");
    if (!host) {
        return null;
    }
    const protocol = isSecure ? "https" : "http";
    // Direct mode uses arc_gateway; fleet mode uses the proxy's path-routed
    // origin. A fleet ARC without a proxy host is unavailable.
    const gatewayBase = gatewayServiceBaseUrl("gimbal", arcId, proxyHost);
    if (gatewayBase) return gatewayBase;
    if (arcId) return null;

    return `${protocol}://${host}:${port}`;
}

/**
 * POST /profile — copies gateway.&lt;profile&gt;.yaml → gateway.yaml on the robot
 * (see r2c2_gimbal_family gimbal_gateway).
 */
export async function postGimbalGatewayProfile(
    baseUrl: string,
    profile: GimbalGatewayProfileId
): Promise<GimbalGatewayProfileResponse> {
    const url = joinUrl(baseUrl, "/profile");
    const res = await authedFetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ profile }),
    });
    const text = await res.text();
    let data: unknown = {};
    try {
        data = text ? JSON.parse(text) : {};
    } catch {
        data = { raw: text };
    }
    if (!res.ok) {
        const detail =
            typeof data === "object" && data !== null && "detail" in data
                ? String((data as { detail: unknown }).detail)
                : text || res.statusText;
        throw new Error(messageFromFailedResponse(res.status, detail, "Couldn't complete the request. Try again."));
    }
    return data as GimbalGatewayProfileResponse;
}

/** GET /profile — reads current active profile from gateway.yaml */
export async function getGimbalGatewayProfile(
    baseUrl: string
): Promise<GimbalGatewayCurrentProfileResponse> {
    const url = joinUrl(baseUrl, "/profile");
    const res = await authedFetch(url);
    const text = await res.text();
    let data: unknown = {};
    try {
        data = text ? JSON.parse(text) : {};
    } catch {
        data = { raw: text };
    }
    if (!res.ok) {
        const detail =
            typeof data === "object" && data !== null && "detail" in data
                ? String((data as { detail: unknown }).detail)
                : text || res.statusText;
        throw new Error(messageFromFailedResponse(res.status, detail, "Couldn't complete the request. Try again."));
    }
    return data as GimbalGatewayCurrentProfileResponse;
}

/** DELETE /profile — removes active gateway.yaml (disconnect current payload profile) */
export async function deleteGimbalGatewayProfile(
    baseUrl: string
): Promise<GimbalGatewayCurrentProfileResponse> {
    const url = joinUrl(baseUrl, "/profile");
    let res = await authedFetch(url, { method: "DELETE" });
    if (res.status === 405) {
        // Some deployments/proxies disallow DELETE; use POST fallback.
        res = await authedFetch(joinUrl(baseUrl, "/profile/disconnect"), {
            method: "POST",
        });
    }
    const text = await res.text();
    let data: unknown = {};
    try {
        data = text ? JSON.parse(text) : {};
    } catch {
        data = { raw: text };
    }
    if (!res.ok) {
        const detail =
            typeof data === "object" && data !== null && "detail" in data
                ? String((data as { detail: unknown }).detail)
                : text || res.statusText;
        throw new Error(messageFromFailedResponse(res.status, detail, "Couldn't complete the request. Try again."));
    }
    return data as GimbalGatewayCurrentProfileResponse;
}

export type ThermalPaletteInfo = {
    name: string;
    wire_id: string;
};

export type ThermalPaletteResponse = {
    ok?: boolean;
    palette?: string | null;
    wire_id?: string | null;
    bytes_sent?: number;
    frame?: string;
    via?: string;
    persisted?: boolean;
};

/** GET /control/thermal_palette — persisted C13 thermal palette (or null if unset) */
export async function getGimbalThermalPalette(
    baseUrl: string
): Promise<ThermalPaletteResponse> {
    const url = joinUrl(baseUrl, "/control/thermal_palette");
    const res = await authedFetch(url);
    const text = await res.text();
    let data: unknown = {};
    try {
        data = text ? JSON.parse(text) : {};
    } catch {
        data = { raw: text };
    }
    if (!res.ok) {
        const detail =
            typeof data === "object" && data !== null && "detail" in data
                ? String((data as { detail: unknown }).detail)
                : text || res.statusText;
        throw new Error(
            messageFromFailedResponse(
                res.status,
                detail,
                "Couldn't complete the request. Try again."
            )
        );
    }
    return data as ThermalPaletteResponse;
}

/** GET /control/thermal_palette/modes — Skydroid C13 ThermalPalette list */
export async function getGimbalThermalPaletteModes(
    baseUrl: string
): Promise<ThermalPaletteInfo[]> {
    const url = joinUrl(baseUrl, "/control/thermal_palette/modes");
    const res = await authedFetch(url);
    const text = await res.text();
    let data: unknown = {};
    try {
        data = text ? JSON.parse(text) : {};
    } catch {
        data = { raw: text };
    }
    if (!res.ok) {
        const detail =
            typeof data === "object" && data !== null && "detail" in data
                ? String((data as { detail: unknown }).detail)
                : text || res.statusText;
        throw new Error(
            messageFromFailedResponse(
                res.status,
                detail,
                "Couldn't complete the request. Try again."
            )
        );
    }
    const modes =
        typeof data === "object" &&
        data !== null &&
        Array.isArray((data as { modes?: unknown }).modes)
            ? (data as { modes: ThermalPaletteInfo[] }).modes
            : [];
    return modes;
}

/** POST /control/thermal_palette — set C13 thermal palette (HTTP → UDP IMG) */
export async function postGimbalThermalPalette(
    baseUrl: string,
    palette: string
): Promise<ThermalPaletteResponse> {
    const url = joinUrl(baseUrl, "/control/thermal_palette");
    const res = await authedFetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ palette }),
    });
    const text = await res.text();
    let data: unknown = {};
    try {
        data = text ? JSON.parse(text) : {};
    } catch {
        data = { raw: text };
    }
    if (!res.ok) {
        const detail =
            typeof data === "object" && data !== null && "detail" in data
                ? String((data as { detail: unknown }).detail)
                : text || res.statusText;
        throw new Error(
            messageFromFailedResponse(
                res.status,
                detail,
                "Couldn't complete the request. Try again."
            )
        );
    }
    return data as ThermalPaletteResponse;
}
